import { render, type VNode } from "preact";
import { defaultThemeInput } from "@cos/shared";
import { BASE_STYLES } from "./theme/styles.js";
import { themeToCss } from "./theme/applyTheme.js";

export interface MountResult {
  setThemeCss: (css: string) => void;
  unmount: () => void;
}

/**
 * Every color in `BASE_STYLES` reads through a `var(--cos-*)` custom
 * property with no fallback — deliberately, so the same structural CSS
 * works for every brand (see that file's own doc comment). The tradeoff is
 * that the panel is invisible (transparent background, unreadable text)
 * for however long it takes `session.ready` to arrive and populate those
 * properties for real — a small gap on a live embed, but enough for the
 * panel to sit open and looking broken during the admin's own preview
 * (before it's even connected). Seeding the theme sheet with the bot
 * platform's own conservative default fills that gap with *something*
 * legible; `setThemeCss` below overwrites it the moment the real theme
 * arrives, same as always.
 */
const PLACEHOLDER_THEME_CSS = themeToCss(defaultThemeInput);

/** Shadow DOM with a constructed stylesheet — CSS inlined, no second request, no leakage either direction. See PLAN.md "Isolation & size". */
export function mount(target: HTMLElement, vnode: VNode): MountResult {
  const host = document.createElement("div");
  target.appendChild(host);
  const shadowRoot = host.attachShadow({ mode: "open" });

  const baseSheet = new CSSStyleSheet();
  baseSheet.replaceSync(BASE_STYLES);
  const themeSheet = new CSSStyleSheet();
  themeSheet.replaceSync(PLACEHOLDER_THEME_CSS);
  shadowRoot.adoptedStyleSheets = [baseSheet, themeSheet];

  const container = document.createElement("div");
  shadowRoot.appendChild(container);
  render(vnode, container);

  return {
    setThemeCss: (css: string) => themeSheet.replaceSync(css),
    unmount: () => {
      render(null, container);
      host.remove();
    },
  };
}
